import Cookies from 'universal-cookie';
import { useRef, useState, createContext } from 'react';
import { authLog, authTrail, fingerprint } from 'src/sentry';

import { postToApp } from 'src/utils/post-to-app';

export interface AuthContextValue {
  auth: string | null | undefined;
  setAuth: (token?: string | null) => void;
  /** Remembers the last revoked token (see comment in AuthProvider). */
  clearedToken: React.MutableRefObject<string | null | undefined>;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

type Props = {
  children?: React.ReactNode;
};

export const AuthProvider = ({ children }: Props) => {
  const cookies = new Cookies();
  const defaultAuth = cookies.get('megi-authorization');

  const [auth, setAuth] = useState<string | null | undefined>(defaultAuth);
  // remembers the token we last revoked so a stale copy still held by the
  // native app isn't passively re-adopted on the login screen
  const clearedToken = useRef<string | null | undefined>(null);

  const handleSetAuth = (token?: string | null) => {
    if (!token) {
      authLog('auth:clear', {
        prevTokenFp: fingerprint(auth),
        route: window.location.pathname,
        nativeWebview: !!window.ReactNativeWebView,
      });
      clearedToken.current = auth;
      cookies.remove('megi-authorization', { path: '/' });
      // token: null is the bridge's logout signal; AppMessage (src/utils/post-to-app)
      // types token as string-only — cast until it is widened to string | null
      postToApp('SET_AUTH_TOKEN', { token: null });
      setAuth(null);
      return;
    }
    authTrail('auth:set', { tokenFp: fingerprint(token), route: window.location.pathname });
    // set age to 1 year
    cookies.set('megi-authorization', token, { path: '/', maxAge: 31556952 });
    setAuth(token);
  };

  return <AuthContext.Provider value={{ auth, setAuth: handleSetAuth, clearedToken }}>{children}</AuthContext.Provider>;
};

export default AuthContext;
